/**
 * UpgradePage.jsx — MK-004 3-tier upgrade page (PR_RUN_023, Stripe live)
 *
 * Per TRL-017 — three tiers (Individual, Team, Enterprise) shown to any
 * user whose account_type is TRIAL/EXPIRING_SOON/EXPIRED.
 *
 * PR_RUN_023 — replaced the previous MOCKED upgrade flow with a real Stripe
 * Checkout integration via the emergentintegrations playbook:
 *   1. "Choose plan" POSTs {target_tier, origin_url} to /api/trial/upgrade
 *   2. Backend creates a Stripe Checkout session, persists a pending
 *      payment_transactions row, and returns {checkout_url, session_id}
 *   3. We redirect to checkout_url (Stripe-hosted checkout)
 *   4. On return Stripe appends ?session_id=cs_test_... — we then poll
 *      /api/trial/checkout/status/{session_id} until payment_status=paid
 *      (or expired/timeout) and navigate to dashboard on success.
 *
 * Enterprise still routes to sales — backend returns 400.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

const TIERS = ['individual', 'team', 'enterprise'];

export default function UpgradePage() {
  const { t } = useTranslation('trial');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const polledRef = useRef(false);

  useEffect(() => {
    async function fetchStatus() {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/trial/status`;
        const res = await fetch(url, { credentials: 'include' });
        if (res.ok) setStatus(await res.json());
      } catch (e) {
        setError(String(e));
      }
    }
    fetchStatus();
  }, []);

  // Stripe return handler — poll payment status if we land here with ?session_id=...
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const cancelled = searchParams.get('cancelled');
    if (cancelled) {
      setPaymentStatus('cancelled');
      setError(t('upgrade.payment_cancelled', { defaultValue: 'Payment was cancelled. You can try again any time.' }));
      return;
    }
    if (!sessionId || polledRef.current) return;
    polledRef.current = true;

    let attempts = 0;
    const MAX_ATTEMPTS = 8;
    const INTERVAL_MS = 2000;
    setPaymentStatus('checking');

    async function poll() {
      attempts += 1;
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/trial/checkout/status/${sessionId}`;
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`status check failed (HTTP ${res.status})`);
        const data = await res.json();
        if (data.payment_status === 'paid') {
          setPaymentStatus('paid');
          setTimeout(() => navigate('/'), 1500);
          return;
        }
        if (data.status === 'expired') {
          setPaymentStatus('expired');
          setError(t('upgrade.payment_expired', { defaultValue: 'Payment session expired. Please start again.' }));
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setPaymentStatus('timeout');
          setError(t('upgrade.payment_timeout', { defaultValue: 'Payment is still processing. Check your email for confirmation.' }));
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        setError(String(e.message || e));
      }
    }
    poll();
  }, [searchParams, navigate, t]);

  async function selectTier(tier) {
    setSelected(tier);
    setBusy(true);
    setError(null);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/trial/upgrade`;
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_tier: tier,
          origin_url: window.location.origin,
        }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `Upgrade failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      if (!data.checkout_url) throw new Error('No checkout_url received from server');
      // Redirect to Stripe-hosted checkout (PR_RUN_023).
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(String(e.message || e));
      setBusy(false);
    }
  }

  const seatLimit = status?.tier_seat_limit ?? 25;

  return (
    <div data-testid="upgrade-page" className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-semibold text-slate-900 mb-2" data-testid="upgrade-title">
          {t('upgrade.page_title')}
        </h1>
        <p className="text-base text-slate-600 mb-2" data-testid="upgrade-subtitle">
          {t('upgrade.page_subtitle')}
        </p>
        <p className="text-sm text-slate-500 mb-8">{t('upgrade.data_will_be_retained')}</p>

        {paymentStatus === 'checking' && (
          <div data-testid="upgrade-payment-checking" className="mb-6 px-4 py-3 bg-blue-50 border border-blue-200 text-blue-900 rounded">
            {t('upgrade.payment_checking', { defaultValue: 'Confirming your payment with Stripe…' })}
          </div>
        )}
        {paymentStatus === 'paid' && (
          <div data-testid="upgrade-payment-success" className="mb-6 px-4 py-3 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded">
            {t('upgrade.payment_success', { defaultValue: 'Payment confirmed — redirecting to your dashboard…' })}
          </div>
        )}
        {error && (
          <div data-testid="upgrade-error" className="mb-6 px-4 py-3 bg-red-50 border border-red-200 text-red-900 rounded">
            {error}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          {TIERS.map((tier) => {
            const tierKey = `tier_${tier}`;
            return (
              <div
                key={tier}
                data-testid={`upgrade-tier-${tier}`}
                className={`bg-white rounded-2xl border-2 p-6 flex flex-col ${
                  selected === tier ? 'border-slate-900' : 'border-slate-200'
                }`}
              >
                <h2 className="text-lg font-semibold text-slate-900 mb-1">
                  {t(`upgrade.${tierKey}_name`)}
                </h2>
                <p className="text-sm text-slate-600 mb-4">
                  {t(`upgrade.${tierKey}_blurb`, tier === 'team' ? { seatLimit } : {})}
                </p>
                <p className="text-xs text-slate-500 italic mb-6">{t('upgrade.pricing_placeholder')}</p>
                <div className="flex-1" />
                <button
                  data-testid={`upgrade-tier-${tier}-cta`}
                  disabled={busy}
                  onClick={() => selectTier(tier)}
                  className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-slate-800 transition disabled:opacity-50"
                >
                  {tier === 'enterprise' ? t('upgrade.tier_enterprise_cta') : t('upgrade.tier_select_cta')}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex justify-center">
          <button
            onClick={() => navigate('/')}
            data-testid="upgrade-back-cta"
            className="text-sm text-slate-600 hover:text-slate-900"
          >
            {t('upgrade.back_to_dashboard')}
          </button>
        </div>
      </div>
    </div>
  );
}
